import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, colors } from "@repo/ui";
import PageContainer from "../../../../../components/PageContainer/PageContainer";
import TwoColumnLayout from "../../../../../components/TwoColumnLayout/TwoColumnLayout";
import { Gift } from "../../../../../services/giftApiService";
import { Product } from "../../../../../services/productApiService";
import EventDetails from "../../AsideSection/EventDetails";
import placeholderImage from "../../../../../assets/placeholders/picture.webp";
import undoIcon from "../../../../../assets/icons/undo.svg";
import { fetchGiftData, formatDate, ThankYouMessage } from "../common";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_components_GiftDetail_web_WebGiftDetail";

const GiftDetailWeb: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const { giftId } = useParams<{ giftId: string }>();
  const navigate = useNavigate();
  const [gift, setGift] = useState<Gift | null>(null);
  const [mediaURL, setMediaURL] = useState<string>(placeholderImage);
  const [isVideo, setIsVideo] = useState(false);
  const [productDetails, setProductDetails] = useState<{
    [id: string]: Product;
  }>({});

  useEffect(() => {
    if (giftId) {
      fetchGiftData(
        giftId,
        setGift,
        setMediaURL,
        setIsVideo,
        setProductDetails
      );
    }
  }, [giftId]);

  if (!gift) {
    return <div>{t("loading")}</div>;
  }

  return (
    <PageContainer startColor={colors.background} endColor="white">
      <div className="p-0 md:p-8 w-[100vw]">
        <TwoColumnLayout
          aside={
            <>
              <EventDetails />
              <Button
                className="w-full"
                type="secondary"
                onClick={() => navigate("/my-table/gifts")}
              >
                <div className="flex items-center justify-between">
                  <span>{t("backToMyGifts")}</span>
                  <img
                    src={undoIcon}
                    alt={t("backAlt")}
                    className="ml-2 h-4 w-4"
                  />
                </div>
              </Button>
            </>
          }
          asideWidth="clamp(300px, 15vw, 10%)"
          stickyTop="0px"
          paddingClass="p-4"
          asideClassName="space-y-8 text-white"
          mainClassName="flex-1"
        >
          <div className="flex flex-col items-center mt-4 space-y-8 bg-white p-6">
            {/* Media (image or video) */}
            <div className="w-48 h-48 md:w-72 md:h-72 flex items-center justify-center mb-4">
              {isVideo ? (
                <video
                  src={mediaURL}
                  controls
                  className="object-cover h-full w-full rounded"
                >
                  {t("videoNotSupported")}
                </video>
              ) : (
                <img
                  src={mediaURL || placeholderImage}
                  alt={t("giftMediaAlt")}
                  className="object-cover h-full w-full rounded-full"
                />
              )}
            </div>

            {/* Donator Info */}
            <p className="text-gray-600 font-bold text-lg mb-2">
              {t("sentBy")}:{" "}
              {gift.IsAnonymous ? t("anonymous") : gift.DonatorName}
            </p>
            <p className="text-gray-500 font-bold mb-4">
              {t("receivedOn")}: {formatDate(gift.GivenDate)}
            </p>

            {/* Gift message */}
            <p className="text-primary italic border-l-4 border-primary pl-4 mb-4">
              {gift.Message || t("noMessageProvided")}
            </p>

            {/* Total Value */}
            <div className="text-gray-800 font-bold text-lg mt-6">
              {t("total")} : ${gift.Value?.toLocaleString()}
            </div>

            {/* Products */}
            <div className="w-full space-y-4 mt-4">
              <h3 className="text-lg font-semibold mb-2">
                {t("giftProducts")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gift.Products.map((product) => {
                  const productDetail = productDetails[product.ID];
                  return (
                    <div
                      key={product.ID}
                      className="flex items-center space-x-4 p-4 border rounded-lg"
                    >
                      <img
                        src={productDetail?.pictures?.[0] || placeholderImage}
                        alt={productDetail?.title || t("productAlt")}
                        className="w-24 h-24 object-cover rounded"
                      />
                      <div className="flex flex-col">
                        <p className="text-gray-700 font-bold">
                          {productDetail?.title}
                        </p>
                        <p className="text-gray-500">
                          {t("quantity")} : {product.Quantity}
                        </p>
                        <p className="text-gray-800 font-semibold">
                          {t("price")} : ${product.Value?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Thank You Message (conditional) */}
            <div className="w-full space-y-4">
              {gift.ThankYouMessage?.trim() ? (
                <>
                  <h3 className="text-lg font-semibold mb-2">
                    {t("youResponded")}:
                  </h3>
                  <ThankYouMessage message={gift.ThankYouMessage} />
                </>
              ) : (
                <div className="text-center mt-8 space-y-4">
                  <p className="text-gray-600 leading-relaxed">
                    {t("noThankYouNote")}
                  </p>
                  <Button
                    className="mx-auto"
                    type="primary"
                    onClick={() =>
                      navigate(`/my-table/gift/${giftId}/thankyoumessage`)
                    }
                  >
                    {t("sendItNow")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TwoColumnLayout>
      </div>
    </PageContainer>
  );
};

export default withTranslations(LOCALE)(GiftDetailWeb);
